import React, { useState } from 'react';
import type { Organization, GlobalAsset, EmergencyPreparednessPlanResult } from '@types';
import { useGlobalAssets } from '../context/GlobalAssetContext';
import { ICONS } from './constants';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { HelpSidebar } from './HelpSidebar';

interface EmergencyPreparednessBuilderProps {
    isOpen: boolean;
    onClose: () => void;
    organization: Organization;
    onComplete: (result: EmergencyPreparednessPlanResult) => void;
    onRequestHelp?: (preferChat?: boolean) => void;
    onNavigate?: (nav: { page: string }) => void;
}

export const EmergencyPreparednessBuilder: React.FC<EmergencyPreparednessBuilderProps> = ({ 
    isOpen, 
    onClose, 
    organization, 
    onComplete, 
    onRequestHelp, 
    onNavigate 
}) => {
    const { assets } = useGlobalAssets();
    const [step, setStep] = useState(1);
    
    // Form State
    const [scenario, setScenario] = useState('Active Shooter / Armed Intruder');
    const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
    const [responseTeam, setResponseTeam] = useState<string[]>([]);
    
    // Engine State
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [loadingText, setLoadingText] = useState('');

    const SCENARIOS = [
        'Active Shooter / Armed Intruder',
        'Bomb Threat / Suspicious Package',
        'Severe Weather (Hurricane / Tornado)',
        'Earthquake / Seismic Event',
        'Fire / Chemical Spill',
        'Medical Emergency / Mass Casualty',
        'Civil Unrest / Protests'
    ];

    const toggleAsset = (id: string) => {
        setSelectedAssetIds(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
    };

    const ROLES = [
        'Floor Warden',
        'Incident Commander',
        'Medical / First Aid Lead',
        'Communications Officer',
        'Security Director',
        'Facility Manager'
    ];

    const toggleRole = (role: string) => {
        setResponseTeam(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
    };

    const handleGenerate = async () => {
        setIsGenerating(true);
        setStep(4); // Loading step
        
        try {
            setLoadingText('Initializing Emergency Architecture Core...');
            setProgress(15);
            await new Promise(r => setTimeout(r, 1200));

            setLoadingText('Locking Scenario and Topography Maps...');
            setProgress(45);
            
            const selectedAssetsData = assets.filter(a => selectedAssetIds.includes(a.id));

            let result: EmergencyPreparednessPlanResult;
            
            const functions = getFunctions();
            const generateEPPlan = httpsCallable(functions, 'generateEmergencyPreparednessPlan');
            const response = await generateEPPlan({
                organizationId: organization.id,
                industry: 'Corporate', // Fallback
                scenario,
                locations: selectedAssetsData,
                responseTeam
            });

            result = response.data as EmergencyPreparednessPlanResult;
            setProgress(85);
            setLoadingText('Drafting Evacuation Routes & Rally Points...');
            await new Promise(r => setTimeout(r, 1000));
            
            setProgress(100);
            setTimeout(() => {
                onComplete(result);
            }, 800);

        } catch (error) {
            console.error("Emergency Preparedness Generation Error", error);
            setLoadingText('ERROR: AI Generation Failed. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in p-4">
            <div className={`absolute inset-0 bg-slate-900/90 backdrop-blur-xl ${!isGenerating ? 'cursor-pointer' : ''}`} onClick={!isGenerating ? onClose : undefined} />
            
            <div className="relative w-full max-w-6xl glass-panel flex flex-col md:flex-row transform transition-transform animate-scale-in text-left shadow-[0_0_100px_rgba(59,130,246,0.3)] border border-blue-500/50 rounded-2xl max-h-[90vh] overflow-hidden">
                
                {/* Main Content Area */}
                <div className="flex-1 p-8 overflow-y-auto custom-scrollbar relative">
                    {/* Header */}
                <div className="flex justify-between items-start mb-8 border-b border-slate-700/50 pb-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-orange-500/20 rounded-lg text-orange-400">
                                {ICONS.shield}
                            </div>
                            <h2 className="text-2xl font-black text-white tracking-widest uppercase">
                                Emergency Preparedness Wizard
                            </h2>
                        </div>
                        <p className="text-sm text-slate-400 font-mono tracking-widest pl-12 uppercase">
                            Pre-plan tactical responses to severe kinetic or natural events.
                        </p>
                    </div>
                    {!isGenerating && (
                        <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 transition-colors">
                            {ICONS.menuClose}
                        </button>
                    )}
                </div>

                {step === 1 && (
                    <div className="space-y-8 animate-fade-in">
                        <div className="space-y-4">
                            <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest">Select Emergency Scenario</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {SCENARIOS.map(s => (
                                    <button
                                        key={s}
                                        onClick={() => setScenario(s)}
                                        className={`p-4 rounded-lg border text-left cursor-pointer transition-all flex items-center justify-between ${scenario === s ? 'bg-orange-600/20 border-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.2)] text-orange-100' : 'bg-slate-800/50 border-slate-700 hover:border-slate-500 text-slate-400'}`}
                                    >
                                        <span className="text-sm font-bold leading-tight">{s}</span>
                                        {scenario === s && (
                                            <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end pt-6 border-t border-slate-800">
                            <button 
                                onClick={() => setStep(2)}
                                className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded shadow-lg shadow-blue-500/20 transition-all uppercase tracking-widest"
                            >
                                Map to Facilities
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-6 animate-fade-in">
                        <div className="flex justify-between items-center">
                            <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest">Target Facilities <span className="text-slate-500">({selectedAssetIds.length} Selected)</span></label>
                            <button onClick={() => setSelectedAssetIds(assets.map(a => a.id))} className="text-xs text-blue-400 hover:text-blue-300 font-bold uppercase tracking-widest">Select All</button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-64 overflow-y-auto custom-scrollbar p-2">
                            {assets.length === 0 && (
                                <div className="col-span-2 text-center py-8 text-slate-500 font-mono text-sm border border-slate-800 border-dashed rounded-lg bg-slate-900/30">
                                    No assets registered globally. Proceeding will generate a generic scenario plan.
                                </div>
                            )}
                            {assets.map(asset => (
                                <div 
                                    key={asset.id} 
                                    onClick={() => toggleAsset(asset.id)}
                                    className={`p-4 rounded-lg border cursor-pointer transition-all flex items-start gap-3 ${selectedAssetIds.includes(asset.id) ? 'bg-orange-600/20 border-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.2)]' : 'bg-slate-800/50 border-slate-700 hover:border-slate-500'}`}
                                >
                                    <input 
                                        type="checkbox" 
                                        checked={selectedAssetIds.includes(asset.id)} 
                                        onChange={() => {}} 
                                        className="mt-1 flex-shrink-0"
                                    />
                                    <div>
                                        <h4 className="font-bold text-slate-200 text-sm leading-tight">{asset.name}</h4>
                                        <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">{asset.type} • {asset.location?.address || 'Unknown Location'}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-between pt-6 border-t border-slate-800">
                            <button 
                                onClick={() => setStep(1)}
                                className="px-6 py-3 border border-slate-600 hover:border-slate-500 text-slate-300 font-bold rounded transition-all uppercase tracking-widest"
                            >
                                Back
                            </button>
                            <button 
                                onClick={() => setStep(3)}
                                className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded shadow-lg shadow-blue-500/20 transition-all uppercase tracking-widest"
                            >
                                Next: Response Team
                            </button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-6 animate-fade-in">
                        <div className="mb-8">
                            <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest mb-4">Key Personnel / Response Team</label>
                            <p className="text-sm text-slate-400 mb-6">Select the key personnel roles that will be responsible for executing this tactical plan.</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {ROLES.map(role => (
                                    <button
                                        key={role}
                                        onClick={() => toggleRole(role)}
                                        className={`p-3 rounded-xl border text-left text-sm transition-all flex items-center justify-between ${
                                            responseTeam.includes(role)
                                            ? 'bg-orange-600/20 border-orange-500/50 text-orange-300 shadow-[0_0_10px_rgba(249,115,22,0.1)]'
                                            : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-800'
                                        }`}
                                    >
                                        <span className="font-semibold">{role}</span>
                                        {responseTeam.includes(role) && (
                                            <div className="w-4 h-4 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs">✓</div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-between pt-6 border-t border-slate-800">
                            <button 
                                onClick={() => setStep(2)}
                                className="px-6 py-3 border border-slate-600 hover:border-slate-500 text-slate-300 font-bold rounded transition-all uppercase tracking-widest"
                            >
                                Back
                            </button>
                            <button 
                                onClick={handleGenerate}
                                className="px-8 py-3 bg-red-600 hover:bg-red-500 text-white font-bold rounded shadow-lg shadow-red-500/20 transition-all uppercase tracking-widest flex items-center gap-2"
                            >
                                {ICONS.shield}
                                Generate Emergency Plan
                            </button>
                        </div>
                    </div>
                )}

                {step === 4 && (
                    <div className="py-20 flex flex-col items-center justify-center animate-fade-in text-center">
                        <div className="mb-8 relative">
                            <div className="absolute inset-0 bg-red-500/20 blur-[50px] rounded-full animate-pulse" />
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-24 h-24 text-red-500 relative z-10 animate-[spin_4s_linear_infinite]">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.678 48.678 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.092 1.21-.138 2.43-.138 3.662v4.159c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.092-1.21.138-2.43.138-3.662v-4.159z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9" />
                            </svg>
                        </div>
                        
                        <p className="text-red-400 font-mono text-sm tracking-widest mb-10 h-6">
                            {loadingText}
                        </p>

                        <div className="w-full max-w-lg bg-slate-800 rounded-full h-2 mb-4 overflow-hidden border border-slate-700">
                            <div 
                                className="bg-gradient-to-r from-red-600 to-orange-400 h-2 rounded-full transition-all duration-500 ease-out relative" 
                                style={{ width: `${progress}%` }}
                            >
                                <div className="absolute top-0 right-0 bottom-0 w-20 bg-white/30 blur-[4px]" />
                            </div>
                        </div>
                    </div>
                )}
                </div>

                {/* Sidebar */}
                <HelpSidebar 
                    title="Emergency Wizard Guidance"
                    description="Pre-plan tactical responses to severe kinetic or natural events."
                    instructions={[
                        "Select the primary Emergency Scenario.",
                        "Map the scenario to specific Target Facilities.",
                        "Select Key Personnel / Response Team roles.",
                        "Click 'Generate Emergency Plan' to produce evacuation routes and checklists."
                    ]}
                    onRequestHelp={onRequestHelp}
                    onContactConsultant={() => onNavigate && onNavigate({ page: 'contact' })}
                    theme="dark"
                />
            </div>
        </div>
    );
};
